
import React, { useState, useEffect } from 'react';
import { ShieldCheck, Search, Plus, Edit2, Trash2, RefreshCw, X, User as UserIcon, Calendar, Loader2 } from 'lucide-react';
import { fetchUsers, saveUserToDB, deleteUserFromDB } from '../services/dataService';
import { User } from '../types';
import AdminUserModal from './AdminUserModal';
import ConfirmModal from './ConfirmModal';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
  theme: 'dark' | 'light';
  currentUser: User;
  onUserUpdate?: (user: User) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ isOpen, onClose, theme, currentUser, onUserUpdate }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  useEffect(() => { if (isOpen) loadUsers(); }, [isOpen]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (error) { console.error("Failed to load users", error); } 
    finally { setIsLoading(false); }
  };

  const handleSaveUser = async (user: User) => {
    const originalUser = users.find(u => u.id === user.id);
    const activeSuperAdminCount = users.filter(u => u.role === 'SUPERADMIN' && u.status === 'ACTIVO').length;

    if (originalUser && originalUser.role === 'SUPERADMIN' && originalUser.status === 'ACTIVO') {
        if ((user.role !== 'SUPERADMIN' || user.status !== 'ACTIVO') && activeSuperAdminCount <= 1) {
            alert("Acción denegada: El sistema requiere al menos un SUPERADMIN activo.");
            return; 
        }
    }

    try {
      await saveUserToDB(user);
      if (onUserUpdate) {
        onUserUpdate(user);
      }
      loadUsers();
    } catch (error) { alert("Error al sincronizar usuario."); }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await deleteUserFromDB(userToDelete.id);
      loadUsers();
    } catch (error) { alert("Error al eliminar usuario."); } 
    finally { setUserToDelete(null); }
  };

  if (!isOpen) return null;

  const filteredUsers = users.filter(u => 
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/90 backdrop-blur-sm z-[150] flex flex-col animate-in fade-in duration-300",
    header: `flex items-center justify-between px-6 py-4 border-b ${theme === 'dark' ? 'bg-[#1a1c22] border-white/10' : 'bg-white border-slate-200'}`,
    content: `flex-1 overflow-auto p-6 ${theme === 'dark' ? 'bg-[#0f0f0f]' : 'bg-slate-50'}`,
    card: `${theme === 'dark' ? 'bg-[#1a1c22] border-[#2d3139]' : 'bg-white border-slate-200'} border rounded-xl shadow-xl overflow-hidden`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
    tableHeader: `${theme === 'dark' ? 'bg-black/20 text-slate-400' : 'bg-slate-50 text-slate-500'} text-[10px] uppercase font-black tracking-wider`,
    row: `${theme === 'dark' ? 'hover:bg-white/5 border-white/5' : 'hover:bg-slate-50 border-slate-100'} border-b transition-colors`,
  };

  return (
    <div className={themeClasses.overlay}>
      <div className={themeClasses.header}>
        <div className="flex items-center space-x-4">
          <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-500 border border-emerald-500/20"><ShieldCheck size={24} /></div>
          <div>
            <h1 className={`text-xl font-black uppercase tracking-tight ${themeClasses.textMain}`}>Administración</h1>
            <p className="text-[10px] font-bold text-slate-500 uppercase">Seguridad y Usuarios</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-full transition-all"><X size={24} className={themeClasses.textMain} /></button>
      </div>

      <div className={themeClasses.content}>
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className={`flex items-center px-4 py-2.5 rounded-xl border w-full sm:w-96 ${theme === 'dark' ? 'bg-[#1a1c22] border-white/10' : 'bg-white border-slate-200'}`}>
              <Search size={16} className="text-slate-500 mr-3" />
              <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`bg-transparent w-full text-sm font-bold outline-none ${themeClasses.textMain}`} />
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={loadUsers} className="p-2.5 rounded-xl border hover:bg-black/5 transition-all"><RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} /></button>
              <button onClick={() => { setEditingUser(null); setIsModalOpen(true); }} className="flex items-center space-x-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] shadow-lg">
                <Plus size={16} /><span>Nuevo Usuario</span>
              </button>
            </div>
          </div>

          <div className={themeClasses.card}>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className={themeClasses.tableHeader}>
                  <tr>
                    <th className="px-6 py-4">Usuario</th>
                    <th className="px-6 py-4">Rol</th>
                    <th className="px-6 py-4">Estado</th>
                    <th className="px-6 py-4 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/5' : 'divide-slate-100'}`}>
                  {isLoading ? (
                    <tr><td colSpan={4} className="py-20 text-center text-xs font-bold">Cargando...</td></tr>
                  ) : (
                    filteredUsers.map((user) => (
                        <tr key={user.id} className={themeClasses.row}>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center text-indigo-500"><UserIcon size={16} /></div>
                              <span className={`font-bold text-sm ${themeClasses.textMain}`}>{user.username}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4"><span className="px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider bg-slate-500/10 text-slate-500">{user.role}</span></td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-1.5">
                              <div className={`w-2 h-2 rounded-full ${user.status === 'ACTIVO' ? 'bg-emerald-500' : 'bg-slate-500'}`}></div>
                              <span className="text-[10px] font-bold uppercase">{user.status}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button onClick={() => { setEditingUser(user); setIsModalOpen(true); }} className="p-1.5 rounded-lg bg-sky-500/10 text-sky-500 hover:bg-sky-500 hover:text-white transition-all"><Edit2 size={14} /></button>
                              <button onClick={() => setUserToDelete(user)} disabled={user.id === currentUser.id} className="p-1.5 rounded-lg bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all disabled:opacity-20"><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <AdminUserModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveUser} userToEdit={editingUser} theme={theme} existingUsers={users} />
      <ConfirmModal isOpen={!!userToDelete} onClose={() => setUserToDelete(null)} onConfirm={handleDeleteUser} title="Eliminar Usuario" message={`¿Deseas eliminar a ${userToDelete?.username}?`} theme={theme} />
    </div>
  );
};

export default AdminPanel;
